<!-- Polaris (fase 5.3, D-C1 + D-C2): the contact as a drawer over the list, in the kit's PersonPanel shape
     (areas/contacts/PersonPanel.vue): three tabs, Details, History and Notes (R2-5-PC-2 = B), the identity at the
     top of each. Width: the kit's person panel, clamp(20rem, 30%, 26rem) (R2-5-PC-11 = A).
     The frame is the shared DrawerFrame (fase P, PROVISÓRIO), not the kit's Inspector: R2-X-3 wants a column from
     1280px and the 3.3 non-modal overlay below it (full width on a phone, R2-5-PC-14), because the drawer opens the
     woot's dialogs and popovers (delete, avatar, labels, new conversation) that a modal Sheet would block, and the
     app's Inspector only knows the modal Sheet as its overlay. The tab header is the Inspector's own markup (same
     classes), with room at the end for DrawerFrame's close button. Pedido para as peças: an Inspector without its
     own frame (or with `modal=false`), so this drawer can go back to it.
     Closing (✕, Escape, a click outside the overlay) only asks the page to close: the page changes the route, and
     the route change is what the discard guard stops (R2-5-PC-4 = B, useDiscardGuard + DiscardChangesAlert). -->
<script setup>
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createReusableTemplate } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import DrawerFrame from 'dashboard/components-next/polaris/cards/DrawerFrame.vue';
import DiscardChangesAlert from 'dashboard/components-next/polaris/cards/DiscardChangesAlert.vue';
import { useDiscardGuard } from 'dashboard/components-next/polaris/cards/useDiscardGuard';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';

const props = defineProps({
  contactId: { type: [String, Number], default: null },
  // the page is loading this contact (contacts/show is not the path: see ContactsIndex)
  loading: { type: Boolean, default: false },
});

// close({ force }): the page closes the drawer (force: without the guard, after a delete or a merge).
const emit = defineEmits(['close']);

const { t } = useI18n();
const [DefineWho, Who] = createReusableTemplate();

const contactById = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const open = computed(() => !!props.contactId);
const record = computed(() =>
  props.contactId ? contactById.value(props.contactId) : null
);
// A list fetch clears the store's records for a moment (another page, a sort): the drawer keeps showing the last
// record of the same contact meanwhile, so Details does not unmount and what was typed in the form stays.
const kept = ref(null);
watch(
  record,
  value => {
    if (value?.id) kept.value = value;
  },
  { immediate: true }
);
const contact = computed(() => {
  if (record.value?.id) return record.value;
  return String(kept.value?.id) === String(props.contactId) ? kept.value : null;
});
const hasContact = computed(() => !!contact.value?.id);
const isLoading = computed(
  () => !hasContact.value && (props.loading || uiFlags.value.isFetchingItem)
);

const tabs = computed(() => [
  { value: 'details', label: t('CONTACTS_POLARIS.DRAWER.TABS.DETAILS') },
  { value: 'history', label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.HISTORY') },
  { value: 'notes', label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.NOTES') },
]);
const tab = ref('details');
// Another contact starts on Details again, whatever the last one was showing (the kit's rule).
watch(
  () => props.contactId,
  () => {
    tab.value = 'details';
  }
);

const label = computed(() =>
  contact.value?.name
    ? t('CONTACTS_POLARIS.DRAWER.LABEL', { name: contact.value.name })
    : t('CONTACTS_POLARIS.DRAWER.LABEL_EMPTY')
);

const detailsRef = ref(null);
const isDirty = () => !!detailsRef.value?.isDirty;

const keepId = `contact-drawer-keep-${useId()}`;
const { confirming, runGuarded, keepEditing, discard } = useDiscardGuard({
  isOpen: () => open.value,
  isDirty,
  close: () => {},
  keepId,
});

defineExpose({ runGuarded, isDirty });
</script>

<template>
  <DefineWho>
    <div
      v-if="contact"
      class="flex min-w-0 items-center gap-3"
      data-contact-who
    >
      <Avatar
        :name="contact.name"
        :src="contact.thumbnail"
        :size="40"
        class="shrink-0"
      />
      <div class="grid min-w-0 flex-1">
        <p class="truncate type-polaris-card-title">{{ contact.name }}</p>
        <p class="truncate type-polaris-caption text-polaris-muted-foreground">
          {{ contact.email || contact.phoneNumber }}
        </p>
      </div>
    </div>
  </DefineWho>

  <DrawerFrame
    :open="open"
    :label="label"
    column-class="w-[clamp(20rem,30%,26rem)]"
    overlay-class="md:max-w-[26rem]"
    data-contact-drawer
    @close="emit('close', { force: false })"
  >
    <Tabs v-model="tab" class="flex min-h-0 flex-1 flex-col gap-0">
      <header
        class="flex h-[4.5rem] shrink-0 items-center justify-between gap-2 border-0 border-b border-solid border-polaris-border px-4 pe-12"
      >
        <TabsList :aria-label="label">
          <TabsTrigger
            v-for="tabItem in tabs"
            :key="tabItem.value"
            :value="tabItem.value"
          >
            {{ tabItem.label }}
          </TabsTrigger>
        </TabsList>
      </header>

      <div v-if="confirming" class="p-4">
        <DiscardChangesAlert
          :keep-id="keepId"
          @keep="keepEditing"
          @discard="discard"
        />
      </div>

      <div
        v-if="isLoading"
        class="grid gap-4 p-4"
        role="status"
        :aria-label="t('CONTACTS_POLARIS.DRAWER.LOADING')"
      >
        <div class="flex items-center gap-3">
          <Skeleton class="size-[72px] rounded-full" />
          <div class="grid flex-1 gap-2">
            <Skeleton class="h-4 w-2/3" />
            <Skeleton class="h-3 w-1/2" />
          </div>
        </div>
        <Skeleton v-for="row in 5" :key="row" class="h-8 w-full" />
      </div>

      <template v-else-if="hasContact">
        <!-- Details stays mounted on the other tabs: its form keeps what was typed (the guard sees it). -->
        <TabsContent
          v-show="!confirming"
          value="details"
          force-mount
          class="min-h-0 flex-1 overflow-auto"
        >
          <ContactDetails
            :key="contact.id"
            ref="detailsRef"
            :selected-contact="contact"
            @go-to-contacts-list="emit('close', { force: true })"
          />
        </TabsContent>
        <TabsContent
          v-show="!confirming"
          value="history"
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-4 py-4">
            <div class="px-4"><Who /></div>
            <ContactHistory />
          </div>
        </TabsContent>
        <TabsContent
          v-show="!confirming"
          value="notes"
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-4 py-4">
            <div class="px-4"><Who /></div>
            <ContactNotes />
          </div>
        </TabsContent>
      </template>
    </Tabs>
  </DrawerFrame>
</template>
